import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

// presto-control-card: a PERSISTENT control dashboard for a Presto display, emitted by the
// device's `control()` helper. Brightness slider (dim → max) + Next/Previous paging.
//
// Persistence is the whole point (the user's science-sims instinct): each control sends a RAW
// `qlink:invoke` ACTION straight to the Presto's station over the chat socket — exactly like
// SimulatorViewer — which qbric routes to the device's `lite` helper WITHOUT the agent running
// it as a chat turn. An `onAction`/assist:action, by contrast, runs as a new turn whose output
// REPLACES the card, so the dashboard would vanish after one interaction.
//
// The slider commits on release (mouseup/touchend), not on every onChange tick, so a drag
// sends ONE set_brightness — not a burst down the mesh.
export default function PrestoControlCard({ structured }) {
  const dispatch = useDispatch();
  const sessionId = useSelector((s) => s.chat && s.chat.station && s.chat.station.sessionId);

  const fns = (structured && structured.functions) || {};
  const station = structured && structured.station;      // this Presto's station id
  const fset = (structured && structured.function_set) || 'lite';
  const uri = (structured && structured.action_uri) || 'qlink:invoke';
  const init = typeof structured?.brightness === 'number' ? structured.brightness : 1.0;
  const [level, setLevel] = React.useState(Math.max(0, Math.min(1, init)));

  const send = (key, params) => {
    const fn = fns[key];
    if (!fn || station == null) return;
    dispatch({
      type: 'WS_SEND',
      payload: {
        messageType: 'ACTION',
        actionUri: uri,
        sessionId,
        params: {
          ref: 'presto_control_' + Date.now(),
          station: String(station),
          class: fset,
          function: fn,
          act_params: JSON.stringify(params || {}),
        },
      },
    });
  };

  const pct = Math.round(level * 100);
  const wrap = { padding: '0 14px' };   // side inset for the header / labels / buttons
  // presto-pending-glyph parity: the hardware turns the tapped glyph AMBER while the frame
  // loads, so a press is visibly acknowledged. These buttons drive the same actions over the
  // mesh and had no pressed state at all — a press looked ignored for the whole round-trip,
  // which invites a second press and pages twice. Same colour as the device's _PENDING pen
  // (224,138,20) so the two surfaces agree about what "working" looks like.
  const btn = {
    flex: 1, fontSize: '0.8rem', fontWeight: 700, padding: '10px 12px',
    border: '1px solid #4b5563', borderRadius: '8px',
    background: '#f9fafb', color: '#111827', cursor: 'pointer',
    transition: 'background 0.12s, color 0.12s, border-color 0.12s',
  };
  const btnPressed = {
    ...btn,
    background: '#e08a14', color: '#ffffff', borderColor: '#b06d10',
  };

  // Which button is mid-flight. Cleared on a timer rather than on an ack: the ACTION is
  // fire-and-forget over the socket (that is what keeps the card persistent), so there is no
  // completion event to listen for. ~600ms is long enough to read and short enough not to
  // linger past the screen actually changing.
  const [pressed, setPressed] = React.useState(null);
  React.useEffect(() => {
    if (!pressed) return undefined;
    const t = setTimeout(() => setPressed(null), 600);
    return () => clearTimeout(t);
  }, [pressed]);

  // Full-bleed: the card fills the (full-width) output drawer, vertical padding only, so the
  // brightness slider (width 100%, no horizontal inset) reaches the screen edges. Header,
  // labels and buttons keep a side inset via `wrap`.
  return (
    <div style={{
      margin: '10px 0', padding: '16px 0', background: '#0f172a', color: '#e2e8f0',
      fontFamily: '"IBM Plex Mono", monospace', width: '100%',
    }}>
      <div style={{
        ...wrap, fontSize: '0.9rem', fontWeight: 700, marginBottom: '14px', textAlign: 'center',
      }}>
        {(structured && structured.displayed_name) || 'Display controls'}
      </div>

      {/* Brightness spectrum — the slider spans edge to edge */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{
          ...wrap, display: 'flex', justifyContent: 'space-between',
          fontSize: '0.75rem', marginBottom: '6px',
        }}>
          <span>Brightness</span>
          <span style={{ fontWeight: 700 }}>{pct}%</span>
        </div>
        <input
          type="range" min="0" max="100" step="1" value={pct}
          onChange={(e) => setLevel(Number(e.target.value) / 100)}
          onMouseUp={() => send('set_brightness', { level })}
          onTouchEnd={() => send('set_brightness', { level })}
          aria-label="Screen brightness"
          style={{ width: '100%', display: 'block', accentColor: '#38bdf8', cursor: 'pointer' }}
        />
        <div style={{
          ...wrap, display: 'flex', justifyContent: 'space-between',
          fontSize: '0.65rem', color: '#94a3b8', marginTop: '4px',
        }}>
          <span>Dim</span>
          <span>Max</span>
        </div>
      </div>

      {/* Card paging */}
      <div style={{ ...wrap, display: 'flex', gap: '8px' }}>
        <button type="button" style={pressed === 'previous' ? btnPressed : btn}
                onClick={() => { setPressed('previous'); send('previous', {}); }}>
          ◀ Previous
        </button>
        <button type="button" style={pressed === 'next' ? btnPressed : btn}
                onClick={() => { setPressed('next'); send('next', {}); }}>
          Next ▶
        </button>
      </div>
    </div>
  );
}
